// Draws a form from its schema: every control type, the style presets (outlined, filled, underline, comb, pill, minimal), colour-coded
// groups, banner, bands, numbered questions and one-box-per-character fields. Used by the public fill page (Drive) and the live preview in
// the designer (Workbook), so both look the same. Self-contained: inline styles plus one scoped <style>, no dependency on an app's CSS.
// The page is drawn light on purpose (a styled form is a document); only the outer margin takes the page colour of the theme.
import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import {
    checkAnswer, choicesOf, combBoxes, dateToDigits, digitsToDate, effectivePreset, groupOf, isInput, joinMulti, questionNumbers, readableOn,
    splitMulti, tint, visibleFlags
} from "./schema";
import type { FormField, FormSchema, FormTheme, Preset } from "./schema";

export interface FormRendererProps {
    schema: FormSchema;
    title: string;
    description?: string | null;
    answers: string[];
    errors: Array<string | null>;
    onChange(index: number, value: string): void;
    onSubmit(): void;
    onReset(): void;
    busy?: boolean;
    /** Preview inside the designer: buttons do nothing. */
    preview?: boolean;
    /** A line under the title ("Responding as ..."). */
    notice?: ReactNode;
    /** Shown above the submit button (a server problem). */
    problem?: string | null;
}

const CSS = `
.fgf-root{--fgf-accent:#2563eb;font-family:system-ui,-apple-system,Roboto,sans-serif;color:#0f172a;padding:24px 12px;min-height:100%}
.fgf-root *{box-sizing:border-box}
.fgf-page{max-width:760px;margin:0 auto}
.fgf-card{background:#fff;border:1px solid #e2e8f0;border-radius:14px;box-shadow:0 1px 3px rgba(15,23,42,.06);padding:22px}
.fgf-grid{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:16px 14px;align-items:start}
.fgf-cell{grid-column:span var(--span,6);min-width:0}
@media (max-width:640px){.fgf-cell{grid-column:span 6}}
.fgf-in:focus,.fgf-comb:focus{box-shadow:0 0 0 3px color-mix(in srgb,var(--fgf-accent) 30%,transparent)}
.fgf-in::placeholder{color:#94a3b8}
.fgf-label{display:block;font-size:13px;font-weight:600;margin-bottom:6px;color:#1e293b}
.fgf-help{font-size:12px;color:#64748b;margin:4px 0 0}
.fgf-err{font-size:12px;color:#dc2626;margin:6px 0 0}
.fgf-btn{font:inherit;font-size:14px;font-weight:600;border:0;cursor:pointer;padding:10px 22px}
.fgf-btn:disabled{opacity:.6;cursor:default}
.fgf-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
`;

type Tone = string | undefined;

function inputStyle(preset: Preset, th: FormTheme, tone: Tone, invalid: boolean): CSSProperties {
    const base: CSSProperties = { width: "100%", font: "inherit", fontSize: 14, color: "#0f172a", outline: "none", padding: "9px 12px", minHeight: 40 };
    const line = invalid ? "#dc2626" : tone ? tint(tone, 0.75) : "#cbd5e1";
    switch (preset) {
        case "filled": { const c = tone ?? th.accent; return { ...base, background: tint(c, 0.12), border: `1.5px solid ${invalid ? "#dc2626" : tint(c, 0.7)}`, borderRadius: th.radius, textAlign: "center" }; }
        case "underline": return { ...base, background: tone ? tint(tone, 0.08) : "transparent", border: "none", borderBottom: `2px solid ${invalid ? "#dc2626" : tone ?? "#94a3b8"}`, borderRadius: 0, padding: "9px 4px" };
        case "comb": return { ...base, background: tone ? tint(tone, 0.1) : "#fff", border: `1px solid ${line}`, borderRadius: Math.min(th.radius, 3) };
        case "pill": return { ...base, background: tone ? tint(tone, 0.1) : "#fff", border: `1px solid ${line}`, borderRadius: 9999, padding: "9px 18px" };
        case "minimal": return { ...base, background: "transparent", border: "none", borderBottom: `1px solid ${invalid ? "#dc2626" : "#e2e8f0"}`, borderRadius: 0, padding: "9px 2px" };
        default: return { ...base, background: tone ? tint(tone, 0.08) : "#fff", border: `1px solid ${line}`, borderRadius: th.radius };
    }
}

function fieldFrame(preset: Preset, th: FormTheme, tone: Tone): CSSProperties {
    const c = tone ?? (preset === "filled" ? th.accent : undefined);
    return { border: `1px solid ${c ? tint(c, 0.6) : "#cbd5e1"}`, borderRadius: preset === "comb" ? Math.min(th.radius, 3) : preset === "pill" ? 18 : th.radius, background: c ? tint(c, 0.08) : "#fff", padding: "10px 12px" };
}

// ---------- one box per character ----------

function Comb({ id, n, text, onText, kind, style, label, groups }: { id: string; n: number; text: string; onText(t: string): void; kind: "text" | "digits"; style: CSSProperties; label: string; groups?: number[] }) {
    const refs = useRef<Array<HTMLInputElement | null>>([]);
    const chars = Array.from({ length: n }, (_, i) => text[i] ?? "");
    const clean = (s: string) => (kind === "digits" ? s.replace(/\D/g, "") : s.toUpperCase());
    const put = (arr: string[]) => onText(arr.join("").replace(/\s+$/, ""));
    const box: CSSProperties = { ...style, width: 32, minHeight: 40, height: 40, padding: 0, textAlign: "center", fontSize: 16, textTransform: "uppercase" };
    return (
        <div role="group" aria-label={label} style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {chars.map((c, i) => (
                <input
                    key={i} id={i === 0 ? id : undefined} ref={(el) => { refs.current[i] = el; }} className="fgf-comb" aria-label={`${label}, character ${i + 1} of ${n}`}
                    inputMode={kind === "digits" ? "numeric" : "text"} autoComplete="off" value={c === " " ? "" : c} style={{ ...box, marginLeft: groups?.includes(i) ? 10 : 0 }}
                    onChange={(e) => {
                        const v = clean(e.target.value).slice(-1);
                        const arr = [...chars]; arr[i] = v === "" ? " " : v;
                        put(arr);
                        if (v && i < n - 1) refs.current[i + 1]?.focus();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Backspace" && !c && i > 0) { e.preventDefault(); const arr = [...chars]; arr[i - 1] = " "; put(arr); refs.current[i - 1]?.focus(); }
                        else if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
                        else if (e.key === "ArrowRight" && i < n - 1) refs.current[i + 1]?.focus();
                    }}
                    onPaste={(e) => {
                        const pasted = clean(e.clipboardData.getData("text")).replace(/[\r\n\t]/g, "");
                        if (!pasted) return;
                        e.preventDefault();
                        const arr = [...chars];
                        for (let k = 0; k < pasted.length && i + k < n; k++) arr[i + k] = pasted[k]!;
                        put(arr);
                        refs.current[Math.min(n - 1, i + pasted.length)]?.focus();
                    }}
                />
            ))}
        </div>
    );
}

/** DD / MM / YYYY boxes; the answer is yyyy-mm-dd once all eight digits are in. */
function DateComb({ id, value, onChange, style, label }: { id: string; value: string; onChange(v: string): void; style: CSSProperties; label: string }) {
    const [digits, setDigits] = useState(dateToDigits(value));
    useEffect(() => { if (value) setDigits(dateToDigits(value)); else setDigits((d) => (d.length === 8 ? "" : d)); }, [value]);
    return <Comb id={id} n={8} kind="digits" text={digits} label={label} style={style} groups={[2, 4]} onText={(t) => { setDigits(t); onChange(digitsToDate(t)); }} />;
}

// ---------- signature ----------

function Signature({ id, value, onChange, frame, height, label }: { id: string; value: string; onChange(v: string): void; frame: CSSProperties; height: number; label: string }) {
    const [mode, setMode] = useState<"type" | "draw">(value.startsWith("drawn:") ? "draw" : "type");
    const canvas = useRef<HTMLCanvasElement | null>(null);
    const drawing = useRef(false);
    const typed = value.startsWith("typed:") ? value.slice(6) : "";
    const pos = (e: React.PointerEvent<HTMLCanvasElement>) => { const r = e.currentTarget.getBoundingClientRect(); return { x: ((e.clientX - r.left) / r.width) * e.currentTarget.width, y: ((e.clientY - r.top) / r.height) * e.currentTarget.height }; };
    useEffect(() => {
        const c = canvas.current;
        if (mode !== "draw" || !c) return;
        const g = c.getContext("2d");
        if (!g) return;
        g.clearRect(0, 0, c.width, c.height);
        if (value.startsWith("drawn:")) { const img = new Image(); img.onload = () => g.drawImage(img, 0, 0); img.src = value.slice(6); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);
    const tab = (m: "type" | "draw", text: string) => (
        <button type="button" onClick={() => setMode(m)} aria-pressed={mode === m} style={{ font: "inherit", fontSize: 12, padding: "3px 10px", border: "1px solid #cbd5e1", background: mode === m ? "#0f172a" : "#fff", color: mode === m ? "#fff" : "#0f172a", cursor: "pointer", borderRadius: 4 }}>{text}</button>
    );
    return (
        <div style={{ ...frame, padding: 8 }}>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>{tab("type", "Type")}{tab("draw", "Draw")}
                {value ? <button type="button" onClick={() => { onChange(""); const c = canvas.current; c?.getContext("2d")?.clearRect(0, 0, c.width, c.height); }} style={{ font: "inherit", fontSize: 12, marginLeft: "auto", background: "none", border: 0, color: "#475569", cursor: "pointer", textDecoration: "underline" }}>Clear</button> : null}
            </div>
            {mode === "type" ? (
                <>
                    <input id={id} className="fgf-in" aria-label={label} placeholder="Type your full name" maxLength={200} value={typed} onChange={(e) => onChange(e.target.value.trim() ? `typed:${e.target.value}` : "")} style={{ width: "100%", border: 0, borderBottom: "1px solid #94a3b8", background: "transparent", font: "inherit", fontSize: 14, padding: "6px 2px", outline: "none" }} />
                    <div aria-hidden style={{ minHeight: Math.max(40, height - 60), fontFamily: "'Segoe Script','Brush Script MT',cursive", fontSize: 28, padding: "8px 2px", color: "#1e3a8a", overflow: "hidden" }}>{typed}</div>
                </>
            ) : (
                <canvas
                    id={id} ref={canvas} width={480} height={Math.max(100, height)} aria-label={`${label}: draw with the mouse or a finger`}
                    style={{ width: "100%", height: Math.max(100, height), background: "#fff", border: "1px dashed #94a3b8", borderRadius: 4, touchAction: "none", cursor: "crosshair" }}
                    onPointerDown={(e) => { drawing.current = true; e.currentTarget.setPointerCapture(e.pointerId); const g = e.currentTarget.getContext("2d")!; const p = pos(e); g.beginPath(); g.moveTo(p.x, p.y); g.lineWidth = 2.5; g.lineCap = "round"; g.strokeStyle = "#1e3a8a"; }}
                    onPointerMove={(e) => { if (!drawing.current) return; const g = e.currentTarget.getContext("2d")!; const p = pos(e); g.lineTo(p.x, p.y); g.stroke(); }}
                    onPointerUp={(e) => { if (!drawing.current) return; drawing.current = false; onChange(`drawn:${e.currentTarget.toDataURL("image/png")}`); }}
                />
            )}
        </div>
    );
}

// ---------- file / photo ----------

function readFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => { const url = String(r.result); resolve(JSON.stringify({ name: file.name, type: file.type || "application/octet-stream", size: file.size, data: url.slice(url.indexOf(",") + 1) })); };
        r.onerror = () => reject(new Error("The file could not be read."));
        r.readAsDataURL(file);
    });
}

function FileInput({ f, id, value, onChange, frame, label, photo }: { f: FormField; id: string; value: string; onChange(v: string): void; frame: CSSProperties; label: string; photo: boolean }) {
    const [msg, setMsg] = useState<string | null>(null);
    type Parsed = { name?: string; type?: string; size?: number; data?: string };
    let parsed = null as Parsed | null;
    try { parsed = value ? (JSON.parse(value) as Parsed) : null; } catch { parsed = null; }
    const maxKb = Math.min(f.maxFileKb ?? 256, 1024);
    const preview = photo && parsed?.data ? `data:image/${(parsed.name ?? "").toLowerCase().endsWith("png") ? "png" : "jpeg"};base64,${parsed.data}` : null;
    return (
        <div style={{ ...frame, minHeight: photo ? f.height ?? 140 : undefined, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, textAlign: "center" }}>
            {preview ? <img src={preview} alt="" style={{ maxHeight: (f.height ?? 140) - 20, maxWidth: "100%", objectFit: "contain" }} /> : null}
            {parsed?.name ? <div style={{ fontSize: 13 }}>{parsed.name} <span style={{ color: "#64748b" }}>({Math.max(1, Math.round((parsed.size ?? 0) / 1024))} KB)</span> <button type="button" onClick={() => { onChange(""); setMsg(null); }} style={{ font: "inherit", fontSize: 12, background: "none", border: 0, color: "#dc2626", cursor: "pointer", textDecoration: "underline" }}>Remove</button></div> : null}
            <label style={{ fontSize: 13, cursor: "pointer", color: "#1d4ed8", textDecoration: "underline" }}>
                {parsed?.name ? "Choose another" : photo ? "Add a photo" : "Choose a file"}
                <input id={id} type="file" className="fgf-sr" aria-label={label} accept={photo ? "image/png,image/jpeg,image/gif,image/webp" : f.accept?.trim() || undefined}
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (!file) return;
                        if (file.size > maxKb * 1024) { setMsg(`The file is larger than ${maxKb} KB.`); return; }
                        void readFile(file).then((v) => { const problem = checkAnswer({ ...f, required: false }, v); if (problem) setMsg(problem); else { setMsg(null); onChange(v); } }).catch((err: Error) => setMsg(err.message));
                    }} />
            </label>
            <div style={{ fontSize: 11, color: "#64748b" }}>Up to {maxKb} KB{f.accept ? `, ${f.accept}` : ""}</div>
            {msg ? <div role="alert" style={{ fontSize: 12, color: "#dc2626" }}>{msg}</div> : null}
        </div>
    );
}

// ---------- the form ----------

export function FormRenderer(p: FormRendererProps) {
    const { schema } = p;
    const th = schema.theme;
    const visible = useMemo(() => visibleFlags(schema.fields, p.answers), [schema.fields, p.answers]);
    const nums = useMemo(() => questionNumbers(schema.fields, visible), [schema.fields, visible]);
    const hasSubmit = schema.fields.some((f) => f.type === "submit");
    const banner = th.banner;
    const bannerInk = banner ? readableOn(banner.color) : "#0f172a";

    const submitButton = (label: string, key: string | number) => (
        <button key={key} type="button" className="fgf-btn" disabled={p.busy} data-form-submit onClick={() => { if (!p.preview) p.onSubmit(); }}
            style={{ background: th.accent, color: readableOn(th.accent), borderRadius: th.preset === "pill" ? 9999 : th.radius }}>{p.busy ? "Sending..." : label}</button>
    );

    return (
        <div className="fgf-root" style={{ ["--fgf-accent" as string]: th.accent, background: th.background ?? "#f1f5f9" }} data-form-root>
            <style>{CSS}</style>
            <div className="fgf-page">
                {banner ? (
                    <div style={{ background: banner.color, color: bannerInk, borderRadius: "14px 14px 0 0", padding: "28px 26px", display: "flex", gap: 18, alignItems: "center", justifyContent: "space-between" }} data-form-banner>
                        <div style={{ minWidth: 0 }}>
                            <h1 style={{ margin: 0, fontSize: 28, lineHeight: 1.15, fontWeight: 800, letterSpacing: "-0.01em" }} data-form-title>{banner.title?.trim() || p.title}</h1>
                            {banner.subtitle ? <p style={{ margin: "8px 0 0", fontSize: 14, opacity: 0.9, whiteSpace: "pre-line" }}>{banner.subtitle}</p> : null}
                        </div>
                        {banner.imageData ? <img src={banner.imageData} alt="" style={{ maxHeight: 72, maxWidth: 140, objectFit: "contain" }} /> : null}
                    </div>
                ) : null}
                <div className="fgf-card" style={banner ? { borderRadius: "0 0 14px 14px", borderTop: 0 } : undefined}>
                    {!banner ? <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }} data-form-title>{p.title}</h1> : null}
                    {p.description ? <p style={{ margin: "8px 0 0", fontSize: 14, color: "#475569", whiteSpace: "pre-line" }}>{p.description}</p> : null}
                    {p.notice ? <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>{p.notice}</div> : null}
                    <p style={{ margin: "12px 0 18px", fontSize: 12, color: "#dc2626" }}>* Required</p>
                    <div className="fgf-grid">
                        {schema.fields.map((f, i) => {
                            if (!visible[i] || f.type === "hidden") return null;
                            const span = Math.min(6, Math.max(1, f.span ?? 6));
                            const g = groupOf(schema, f);
                            const tone = g?.color;
                            return (
                                <div key={f.id} className="fgf-cell" style={{ ["--span" as string]: span }} data-question={i} data-field={f.id}>
                                    {isInput(f.type)
                                        ? <Question f={f} th={th} value={p.answers[i] ?? ""} error={p.errors[i] ?? null} number={nums[i] ?? null} tone={tone} group={g?.name}
                                            onChange={(v) => p.onChange(i, v)} />
                                        : <Layout f={f} th={th} tone={tone} onSubmit={() => { if (!p.preview) p.onSubmit(); }} onReset={() => { if (!p.preview) p.onReset(); }} busy={!!p.busy} />}
                                </div>
                            );
                        })}
                    </div>
                    {p.problem ? <p role="alert" style={{ color: "#dc2626", fontSize: 13, marginTop: 14 }}>{p.problem}</p> : null}
                    {!hasSubmit ? <div style={{ marginTop: 22 }}>{submitButton("Submit", "default")}</div> : null}
                </div>
            </div>
        </div>
    );
}

function Layout({ f, th, tone, onSubmit, onReset, busy }: { f: FormField; th: FormTheme; tone: Tone; onSubmit(): void; onReset(): void; busy: boolean }) {
    const radius = th.preset === "pill" ? 9999 : th.radius;
    switch (f.type) {
        case "section":
            return <div><h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: tone ?? "#0f172a" }}>{f.title}</h2>{f.help ? <p className="fgf-help" style={{ fontSize: 13 }}>{f.help}</p> : null}</div>;
        case "band": {
            const c = f.color ?? tone ?? tint(th.accent, 0.18);
            return <div style={{ background: c, color: readableOn(c), padding: "8px 14px", fontSize: 13, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", borderRadius: Math.min(radius, 6) }} role="heading" aria-level={2}>{f.title}</div>;
        }
        case "divider": return <hr style={{ border: 0, borderTop: "1px solid #cbd5e1", margin: "4px 0" }} />;
        case "spacer": return <div aria-hidden style={{ height: f.height ?? 24 }} />;
        case "image": return f.imageData ? <img src={f.imageData} alt={f.title} style={{ maxWidth: "100%", maxHeight: f.height ?? 240, display: "block", margin: "0 auto" }} /> : null;
        case "stamp": {
            const c = f.color ?? tone ?? "#64748b";
            return <div style={{ minHeight: f.height ?? 90, border: `2px dashed ${c}`, borderRadius: Math.min(radius, 8), display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 8, fontSize: 12, color: c, textAlign: "center" }}>{f.title}</div>;
        }
        case "submit": return <button type="button" className="fgf-btn" disabled={busy} data-form-submit onClick={onSubmit} style={{ background: f.color ?? th.accent, color: readableOn(f.color ?? th.accent), borderRadius: radius }}>{busy ? "Sending..." : f.title}</button>;
        case "reset": return <button type="button" className="fgf-btn" onClick={onReset} style={{ background: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: radius }}>{f.title}</button>;
        case "link": return <a className="fgf-btn" href={f.url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", textDecoration: "none", background: "#fff", color: th.accent, border: `1px solid ${th.accent}`, borderRadius: radius }}>{f.title}</a>;
        default: return null;
    }
}

const TEXTLIKE = new Set(["text", "email", "phone", "url", "password", "number", "date", "time"]);

function Question({ f, th, value, error, number, tone, group, onChange }: { f: FormField; th: FormTheme; value: string; error: string | null; number: number | null; tone: Tone; group?: string; onChange(v: string): void }) {
    const preset = effectivePreset(th, f);
    const id = `fgf-${f.id}`;
    const invalid = !!error;
    const style = inputStyle(preset, th, tone, invalid);
    const frame = fieldFrame(preset, th, tone);
    const [focus, setFocus] = useState(false);
    const comb = !!f.comb && (f.type === "text" || f.type === "number" || f.type === "phone" || f.type === "date");
    const textlike = TEXTLIKE.has(f.type) && !comb;
    const lp = th.labelPosition;
    const inside = lp === "inside" && textlike;
    const floating = lp === "floating" && textlike;
    const left = lp === "left";

    const labelText = (
        <>{number != null ? <span style={{ color: tone ?? th.accent, marginRight: 6 }}>{number}.</span> : null}{f.title}{f.required ? <span style={{ color: "#dc2626" }}> *</span> : null}</>
    );
    const control = <Control f={f} th={th} preset={preset} id={id} value={value} onChange={onChange} style={style} frame={frame} invalid={invalid} comb={comb} tone={tone}
        placeholder={inside ? f.placeholder || f.title : floating ? undefined : f.placeholder} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} />;

    const help = f.help ? <p className="fgf-help">{f.help}</p> : null;
    const err = error ? <p role="alert" className="fgf-err">{error}</p> : null;
    const tag = group ? <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: tone, textTransform: "uppercase", letterSpacing: "0.05em" }}>{group}</span> : null;

    if (floating) {
        const up = focus || value !== "" || f.type === "date" || f.type === "time";
        return (
            <div>
                <div style={{ position: "relative" }}>
                    <label htmlFor={id} style={{ position: "absolute", left: preset === "pill" ? 18 : 12, top: up ? 3 : 11, fontSize: up ? 10 : 14, color: "#64748b", pointerEvents: "none", transition: "all .12s", zIndex: 1 }}>{labelText}</label>
                    <div style={{ paddingTop: up ? 8 : 0 }}>{control}</div>
                </div>
                {help}{err}
            </div>
        );
    }
    if (inside) {
        return <div><label htmlFor={id} className="fgf-sr">{f.title}{f.required ? " (required)" : ""}</label>{control}{help}{err}</div>;
    }
    if (left) {
        return (
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0,32%) minmax(0,1fr)", gap: 10, alignItems: "start" }}>
                <label htmlFor={id} className="fgf-label" style={{ marginBottom: 0, paddingTop: 10 }}>{labelText}{tag}</label>
                <div>{control}{help}{err}</div>
            </div>
        );
    }
    return (
        <div>
            <label htmlFor={id} className="fgf-label">{labelText}{tag}</label>
            {control}{help}{err}
        </div>
    );
}

interface ControlProps {
    f: FormField; th: FormTheme; preset: Preset; id: string; value: string; onChange(v: string): void; style: CSSProperties; frame: CSSProperties;
    invalid: boolean; comb: boolean; tone: Tone; placeholder?: string; onFocus(): void; onBlur(): void;
}

function Control({ f, th, preset, id, value, onChange, style, frame, invalid, comb, tone, placeholder, onFocus, onBlur }: ControlProps) {
    const common = { id, className: "fgf-in", style, "aria-required": f.required || undefined, "aria-invalid": invalid || undefined, placeholder, onFocus, onBlur } as const;
    const choices = choicesOf(f);
    const accent = tone ?? th.accent;
    const label = f.title;
    const [otherOn, setOtherOn] = useState(false);

    if (comb) {
        if (f.type === "date") return <DateComb id={id} value={value} onChange={onChange} style={inputStyle(preset === "outlined" ? "comb" : preset, th, tone, invalid)} label={label} />;
        return <Comb id={id} n={combBoxes(f)} kind={f.type === "number" || f.type === "phone" ? "digits" : "text"} text={value} onText={onChange} label={label} style={inputStyle(preset === "outlined" ? "comb" : preset, th, tone, invalid)} />;
    }

    switch (f.type) {
        case "text": return <input {...common} type="text" maxLength={f.maxLength} value={value} onChange={(e) => onChange(e.target.value)} />;
        case "email": return <input {...common} type="email" maxLength={f.maxLength} value={value} onChange={(e) => onChange(e.target.value)} />;
        case "phone": return <input {...common} type="tel" maxLength={f.maxLength ?? 25} value={value} onChange={(e) => onChange(e.target.value)} />;
        case "url": return <input {...common} type="url" maxLength={f.maxLength} value={value} onChange={(e) => onChange(e.target.value)} />;
        case "password": return <input {...common} type="password" autoComplete="off" maxLength={f.maxLength} value={value} onChange={(e) => onChange(e.target.value)} />;
        case "number": return <input {...common} type="number" inputMode={f.wholeNumber ? "numeric" : "decimal"} min={f.min} max={f.max} step={f.step ?? (f.wholeNumber ? 1 : "any")} value={value} onChange={(e) => onChange(e.target.value)} />;
        case "date": return <input {...common} type="date" min={f.minValue} max={f.maxValue} value={value} onChange={(e) => onChange(e.target.value)} />;
        case "time": return <input {...common} type="time" min={f.minValue} max={f.maxValue} value={value} onChange={(e) => onChange(e.target.value)} />;
        case "textarea": return <textarea {...common} rows={4} maxLength={f.maxLength} value={value} onChange={(e) => onChange(e.target.value)} style={{ ...style, height: "auto", resize: "vertical", textAlign: "left" }} />;
        case "richtext":
            return (
                <div>
                    <textarea {...common} rows={9} maxLength={f.maxLength ?? 20000} value={value} onChange={(e) => onChange(e.target.value)} style={{ ...style, height: "auto", resize: "vertical", textAlign: "left" }} />
                    <div style={{ fontSize: 11, color: "#64748b", textAlign: "right", marginTop: 2 }}>{value.length}{f.maxLength ? ` / ${f.maxLength}` : ""}</div>
                </div>
            );
        case "select":
            return (
                <>
                    <select {...common} value={otherOn || (value !== "" && !choices.includes(value)) ? "__other__" : value} onChange={(e) => { if (e.target.value === "__other__") { setOtherOn(true); onChange(""); } else { setOtherOn(false); onChange(e.target.value); } }} style={{ ...style, textAlign: "left" }}>
                        <option value="">Choose</option>
                        {choices.map((c) => <option key={c} value={c}>{c}</option>)}
                        {f.allowOther ? <option value="__other__">Other...</option> : null}
                    </select>
                    {f.allowOther && (otherOn || (value !== "" && !choices.includes(value))) ? <input aria-label={`${label}, other`} className="fgf-in" style={{ ...style, marginTop: 6, textAlign: "left" }} placeholder="Please specify" maxLength={200} value={choices.includes(value) ? "" : value} onChange={(e) => onChange(e.target.value)} /> : null}
                </>
            );
        case "multiselect":
            return (
                <select id={id} multiple size={Math.min(6, Math.max(3, choices.length))} className="fgf-in" aria-required={f.required || undefined} aria-invalid={invalid || undefined} style={{ ...style, textAlign: "left", padding: 6 }}
                    value={splitMulti(value)} onChange={(e) => onChange(joinMulti(Array.from(e.target.selectedOptions, (o) => o.value)))}>
                    {choices.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
            );
        case "radio": {
            const other = value !== "" && !choices.includes(value);
            return (
                <div role="radiogroup" aria-labelledby={undefined} aria-label={label} id={id} style={{ ...frame, display: "flex", flexDirection: "column", gap: 8 }}>
                    {choices.map((c) => <label key={c} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}><input type="radio" name={id} checked={value === c} onChange={() => { setOtherOn(false); onChange(c); }} style={{ accentColor: accent }} /> {c}</label>)}
                    {f.allowOther ? (
                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                            <input type="radio" name={id} checked={other || otherOn} onChange={() => { setOtherOn(true); onChange(""); }} style={{ accentColor: accent }} /> Other
                            <input aria-label={`${label}, other`} className="fgf-in" maxLength={200} value={other ? value : ""} onFocus={() => setOtherOn(true)} onChange={(e) => onChange(e.target.value)} style={{ ...style, minHeight: 32, padding: "4px 8px", width: "auto", flex: 1 }} />
                        </label>
                    ) : null}
                </div>
            );
        }
        case "checkboxes": {
            const picked = splitMulti(value);
            const extra = picked.find((x) => !choices.includes(x)) ?? "";
            const toggle = (c: string) => onChange(joinMulti(picked.includes(c) ? picked.filter((x) => x !== c) : [...picked, c]));
            return (
                <div role="group" aria-label={label} id={id} style={{ ...frame, display: "flex", flexWrap: "wrap", gap: "8px 18px" }}>
                    {choices.map((c) => <label key={c} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}><input type="checkbox" checked={picked.includes(c)} onChange={() => toggle(c)} style={{ accentColor: accent }} /> {c}</label>)}
                    {f.allowOther ? (
                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                            <input type="checkbox" checked={!!extra} onChange={() => onChange(joinMulti(extra ? picked.filter((x) => x !== extra) : [...picked, "Other"]))} style={{ accentColor: accent }} /> Other
                            <input aria-label={`${label}, other`} className="fgf-in" maxLength={200} value={extra === "Other" ? "" : extra} onChange={(e) => { const t = e.target.value.replace(/\n/g, " "); onChange(joinMulti([...picked.filter((x) => x !== extra), t || "Other"])); }} style={{ ...style, minHeight: 30, padding: "3px 8px", width: 160 }} />
                        </label>
                    ) : null}
                </div>
            );
        }
        case "toggle": {
            const on = value === "Yes";
            return (
                <button id={id} type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(on ? "No" : "Yes")} onFocus={onFocus} onBlur={onBlur}
                    style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "none", border: 0, padding: 0, font: "inherit", fontSize: 14, cursor: "pointer" }}>
                    <span style={{ width: 44, height: 24, borderRadius: 12, background: on ? accent : "#cbd5e1", position: "relative", transition: "background .12s", border: invalid ? "1px solid #dc2626" : "1px solid transparent" }}>
                        <span style={{ position: "absolute", top: 2, left: on ? 22 : 2, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left .12s", boxShadow: "0 1px 2px rgba(0,0,0,.3)" }} />
                    </span>
                    {value === "" ? <span style={{ color: "#94a3b8" }}>Not answered</span> : on ? "Yes" : "No"}
                </button>
            );
        }
        case "rating": {
            const max = Math.min(10, Math.max(1, Math.trunc(f.max ?? 5)));
            const n = Number(value) || 0;
            return (
                <div id={id} role="radiogroup" aria-label={label} style={{ display: "flex", gap: 2 }}>
                    {Array.from({ length: max }, (_, i) => i + 1).map((s) => (
                        <button key={s} type="button" role="radio" aria-checked={n === s} aria-label={`${s} of ${max}`} onClick={() => onChange(n === s ? "" : String(s))}
                            style={{ background: "none", border: 0, cursor: "pointer", fontSize: 30, lineHeight: 1, padding: "0 1px", color: s <= n ? "#f59e0b" : "#cbd5e1" }}>★</button>
                    ))}
                </div>
            );
        }
        case "scale": {
            const lo = Math.trunc(f.min ?? 1), hi = Math.trunc(f.max ?? 10);
            const steps = Array.from({ length: Math.max(0, hi - lo + 1) }, (_, i) => lo + i);
            return (
                <div>
                    <div id={id} role="radiogroup" aria-label={label} style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {steps.map((s) => (
                            <button key={s} type="button" role="radio" aria-checked={value === String(s)} onClick={() => onChange(value === String(s) ? "" : String(s))}
                                style={{ minWidth: 36, height: 36, font: "inherit", fontSize: 14, cursor: "pointer", border: `1px solid ${value === String(s) ? accent : "#cbd5e1"}`, background: value === String(s) ? accent : "#fff", color: value === String(s) ? readableOn(accent) : "#0f172a", borderRadius: preset === "pill" ? 18 : Math.min(th.radius, 8) }}>{s}</button>
                        ))}
                    </div>
                    {f.minLabel || f.maxLabel ? <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b", marginTop: 4 }}><span>{f.minLabel}</span><span>{f.maxLabel}</span></div> : null}
                </div>
            );
        }
        case "slider": {
            const lo = f.min ?? 0, hi = f.max ?? 100;
            return (
                <div>
                    <input id={id} type="range" min={lo} max={hi} step={f.step ?? 1} value={value === "" ? String(f.defaultValue ?? lo) : value} aria-label={label} onFocus={onFocus} onBlur={onBlur}
                        onChange={(e) => onChange(e.target.value)} style={{ width: "100%", accentColor: accent }} />
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b" }}><span>{f.minLabel ?? lo}</span><strong style={{ color: "#0f172a", fontSize: 13 }}>{value === "" ? "move to answer" : value}</strong><span>{f.maxLabel ?? hi}</span></div>
                </div>
            );
        }
        case "file": case "photo": return <FileInput f={f} id={id} value={value} onChange={onChange} frame={frame} label={label} photo={f.type === "photo"} />;
        case "signature": return <Signature id={id} value={value} onChange={onChange} frame={frame} height={f.height ?? 120} label={label} />;
        default: return null;
    }
}

